// src/context/CartContext.jsx
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import axios from 'axios';
import configs from '../config/config';
import { toast } from 'react-hot-toast';

// ────────────────────────────────────────────────
// Config & Constants
// ────────────────────────────────────────────────
const CACHE_KEY_CART = 'cart_cache_v4';
const CACHE_TTL_MS = 30*60 * 1000;

// ────────────────────────────────────────────────
// Initial state
// ────────────────────────────────────────────────
const initialState = {
  cartItems: [],
  loading: true,
  error: null,
  syncError: null,
  lastSync: null,
  cartVersion: 0,
};

// ────────────────────────────────────────────────
// Action Types
// ────────────────────────────────────────────────
const ActionTypes = {
  SET_CART: 'SET_CART',
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  ROLLBACK_QUANTITY: 'ROLLBACK_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_SYNC_ERROR: 'SET_SYNC_ERROR',
};

// ────────────────────────────────────────────────
// Reducer
// ────────────────────────────────────────────────
const cartReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_CART:
      return {
        ...state,
        cartItems: action.payload || [],
        loading: false,
        error: null,
        syncError: null,
        lastSync: Date.now(),
        cartVersion: state.cartVersion + 1,
      };

    case ActionTypes.ADD_ITEM: {
      const { product, quantity = 1, tempId } = action.payload;
      const existingIndex = state.cartItems.findIndex(
        (item) => item.product_id === product.id
      );

      if (existingIndex !== -1) {
        const updatedItems = [...state.cartItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: parseInt((updatedItems[existingIndex].quantity || 0)) + parseInt(quantity),
        };
        return {
          ...state,
          cartItems: updatedItems,
          cartVersion: state.cartVersion + 1,
        };
      }
      if (existingIndex == 1) {
        const updatedItems = [...state.cartItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: parseInt((updatedItems[existingIndex].quantity || 0)) + parseInt(quantity),
        };
        return {
          ...state,
          cartItems: updatedItems,
          cartVersion: state.cartVersion + 1,
        };
      }
      if (existingIndex > 1) {
        const updatedItems = [...state.cartItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: parseInt(quantity),
        };
        return {
          ...state,
          cartItems: updatedItems,
          cartVersion: state.cartVersion + 1,
        };
      }

      return {
        ...state,
        cartItems: [
          ...state.cartItems,
          {
            id: tempId || `temp-${Date.now()}`,
            product_id: product.id,
            quantity,
            name: product.name || 'Product',
            price: product.price || 0,
            image: product.image || '',
          },
        ],
        cartVersion: state.cartVersion + 1,
      };
    }

    case ActionTypes.UPDATE_ITEM: {
      const { productId, newQuantity } = action.payload;
      return {
        ...state,
        cartItems: state.cartItems.map((item) =>
          item.product_id === productId
            ? { ...item, quantity: Math.max(1, newQuantity) }
            : item
        ),
        cartVersion: state.cartVersion + 1,
      };
    }

    case ActionTypes.REMOVE_ITEM: {
      const { productId } = action.payload;
      return {
        ...state,
        cartItems: state.cartItems.filter((item) => item.product_id !== productId),
        cartVersion: state.cartVersion + 1,
      };
    }

    case ActionTypes.ROLLBACK_QUANTITY: {
      const { productId, previousQuantity } = action.payload;
      return {
        ...state,
        cartItems: state.cartItems.map((item) =>
          item.product_id === productId
            ? { ...item, quantity: previousQuantity }
            : item
        ),
      };
    }

    case ActionTypes.CLEAR_CART:
      return {
        ...initialState,
        loading: false,
      };

    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ActionTypes.SET_SYNC_ERROR:
      return { ...state, syncError: action.payload };

    default:
      return state;
  }
};

// ────────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────────
export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const tokenRef = useRef(localStorage.getItem('token'));

  // ─── Cache Helpers ──────────────────────────────────────
  const getCachedCart = useCallback(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY_CART);
      if (!raw) return null;
      const { data, ts } = JSON.parse(raw);
      if (Date.now() - ts < CACHE_TTL_MS) return data;
      localStorage.removeItem(CACHE_KEY_CART);
      return null;
    } catch {
      localStorage.removeItem(CACHE_KEY_CART);
      return null;
    }
  }, []);

  const setCachedCart = useCallback((data) => {
    try {
      localStorage.setItem(
        CACHE_KEY_CART,
        JSON.stringify({
          data,
          ts: Date.now(),
        })
      );
    } catch {}
  }, []);

  // ─── Fetch Cart ─────────────────────────────────────────
  const fetchCart = useCallback(
    async ({ force = false, silent = false } = {}) => {
      if (!silent) dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      if (!force) {
        const cached = getCachedCart();
        if (cached) {
          dispatch({ type: ActionTypes.SET_CART, payload: cached });
        }
      }


      try {
        const res = await axios.get(`${configs.API_URL}/api/collectcart`, {
          headers: {
            Authorization: `Bearer ${tokenRef.current}`,
            Accept: 'application/json',
          },
          withCredentials: true,
        });

        const freshItems = res.data.cartItems || [];
        dispatch({ type: ActionTypes.SET_CART, payload: freshItems });
        setCachedCart(freshItems);
      } catch (err) {
        //console.error('Cart fetch failed:', err);
        dispatch({ type: ActionTypes.SET_SYNC_ERROR, payload: 'Could not sync cart' });
      } finally {
        if (!silent) dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    },
    [getCachedCart, setCachedCart]
  );

  // ─── Lifecycle ──────────────────────────────────────────
  useEffect(() => {
    const currentToken = localStorage.getItem('token');
    tokenRef.current = currentToken;

    if (!currentToken) {
      dispatch({ type: ActionTypes.CLEAR_CART });
      return;
    }

    fetchCart({ force: false, silent: true });

    const handleFocus = () => {
      const newToken = localStorage.getItem('token');
      if (newToken !== tokenRef.current) {
        tokenRef.current = newToken;
        fetchCart({ force: true, silent: true });
      } else {
        fetchCart({ force: false, silent: true });
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchCart]);

  // ─── Add to Cart ────────────────────────────────────────
  const addToCart = useCallback(
    async (product, quantity = 1) => {
      if (!product?.id) return;

      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      dispatch({
        type: ActionTypes.ADD_ITEM,
        payload: { product, quantity, tempId },
      });

      try {
        await axios.get(`${configs.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

        const xsrfToken = document.cookie
          .split('; ')
          .find((row) => row.startsWith('XSRF-TOKEN='))
          ?.split('=')[1];

        await axios.post(
          `${configs.API_URL}/api/addToCart2/${product.id}`,
          {
            product_id: product.id,
            quantity,
            session: localStorage.getItem('session_id') || `guest-${Date.now()}`,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
              Authorization: tokenRef.current ? `Bearer ${tokenRef.current}` : undefined,
            },
            withCredentials: true,
          }
        );

        await fetchCart({ force: true, silent: true });
        toast.success('Added to cart!');
      } catch (err) {
        //console.error('Add failed:', err);
        dispatch({
          type: ActionTypes.REMOVE_ITEM,
          payload: { productId: product.id },
        });
        toast.error('Failed to add to cart');
      }
    },
    [fetchCart]
  );

  // ─── Change Quantity ────────────────────────────────────
 const changeQuantity = useCallback(
  async (productId, delta,product=null) => {

    const item = (state.cartItems || []).find((i) => i.product_id === productId);
    //if (!item) return;
    

    const previousQuantity = Number(item?.quantity) || 0; // safer than parseInt
    const parsedDelta = Number(delta);                   // safer conversion

    let newQuantity;

    // ── Special case: +1 or -1 → treat as increment/decrement ──
    if (parsedDelta === 1 || parsedDelta === -1) {
      newQuantity = Math.max(1, previousQuantity + parsedDelta);
    } 
    // ── All other cases → treat delta as absolute target quantity ──
    else {
      newQuantity = Math.max(1, parsedDelta);   // or Math.max(0, ...) if you allow 0
    }

    // Optional: prevent pointless updates
    if (newQuantity === previousQuantity) return;

    if(!item){
     // addToCart(productId,delta,'variants');
      if(product && product!==null && product!=='null'){
       const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
     

      dispatch({
        type: ActionTypes.ADD_ITEM,
        payload: { product, newQuantity, tempId },
      });

         dispatch({
        type: ActionTypes.UPDATE_ITEM,
        payload: { productId, newQuantity },
      });
       }
      console.log(productId,delta,state.cartItems,product);
     return;
    }

    // ── Optimistic update ──
    dispatch({
      type: ActionTypes.UPDATE_ITEM,
      payload: { productId, newQuantity },
    });

    try {
      await axios.get(`${configs.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      await axios.post(
        `${configs.API_URL}/api/updateCartItemQuantity/${productId}`,
        {
          product_id: productId,
          quantity: newQuantity,
         // session: localStorage.getItem('session_id') || `guest-${Date.now()}`,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
            Authorization: tokenRef.current ? `Bearer ${tokenRef.current}` : undefined,
          },
          withCredentials: true,
        }
      );
setTimeout(function(){
fetchCart({ force: true, silent: true });
    },100);
    } catch (err) {
      console.error('Quantity update failed:', err);
      dispatch({
        type: ActionTypes.ROLLBACK_QUANTITY,
        payload: { productId, previousQuantity },
      });
      toast.error('Failed to update quantity');
    }
  },
  [state.cartItems, fetchCart]
);

  // ─── Remove Item ────────────────────────────────────────
  const removeItem = useCallback(
    async (productId) => {
      const item = (state.cartItems || []).find((i) => i.product_id === productId);
      if (!item) return;

      dispatch({
        type: ActionTypes.REMOVE_ITEM,
        payload: { productId },
      });

      try {
        await axios.get(`${configs.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

        const xsrfToken = document.cookie
          .split('; ')
          .find((row) => row.startsWith('XSRF-TOKEN='))
          ?.split('=')[1];

        await axios.post(
          `${configs.API_URL}/api/removeCartItem`,
          { product_id: productId },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
              Authorization: tokenRef.current ? `Bearer ${tokenRef.current}` : undefined,
            },
            withCredentials: true,
          }
        );

        await fetchCart({ force: true, silent: true });
        toast.success('Item removed');
      } catch (err) {
        //console.error('Remove failed:', err);
        toast.error('Failed to remove item');
        // Optional: re-fetch to recover
        fetchCart({ force: true, silent: true });
      }
    },
    [state.cartItems, fetchCart]
  );

  // ─── Clear Cart ─────────────────────────────────────────
  const clearCart = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_CART });
    localStorage.removeItem(CACHE_KEY_CART);
  }, []);

  // ─── Derived values (memoized) ──────────────────────────
  const cartCount = useMemo(
    () => (state.cartItems || []).reduce((sum, item) => sum + (item?.quantity || 0), 0),
    [state.cartItems]
  );

  const cartTotal = useMemo(
    () =>
      (state.cartItems || []).reduce(
        (sum, item) => sum + (item?.price || 0) * (item?.quantity || 0),
        0
      ),
    [state.cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems: state.cartItems || [],
      cartCount,
      cartTotal,
      loading: state.loading,
      error: state.error,
      syncError: state.syncError,
      lastSync: state.lastSync,
      cartVersion: state.cartVersion,
      addToCart,
      changeQuantity,
      removeItem,
      clearCart,
      isInCart: (productId) =>
        (state.cartItems || []).some((item) => item.product_id === productId),
     getQuantity: (productId) => {
  // Convert input to number — handles string "870", number 870, even " 870 " safely
  const searchId = Number(productId);

  // Optional safety (if someone passes garbage like "abc")
  if (isNaN(searchId)) {
    ////console.warn(`getQuantity received invalid ID: ${productId}`);
    return 0;
  }

  const item = state.cartItems?.find(
    (item) => Number(item.product_id) === searchId
  );

  // Optional extra debug (remove later)
  ////console.log(`getQuantity(${productId}) → found: ${!!item}, qty: ${item?.quantity}`);

  return item?.quantity ?? 0;
},
    }),
    [
      state.cartItems,
      state.loading,
      state.error,
      state.syncError,
      state.lastSync,
      state.cartVersion,
      cartCount,
      cartTotal,
      addToCart,
      changeQuantity,
      removeItem,
      clearCart,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};